import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

const EVENT_ICONS = {
  enrichment_progress: 'fa-brain text-purple-500',
  mining_progress: 'fa-satellite-dish text-primary',
  notification: 'fa-bell text-secondary',
  lead_update: 'fa-user text-accent',
  connected: 'fa-plug text-primary',
};

export default function LiveActivityFeed({ events = [], connected }) {
  const [expanded, setExpanded] = useState(false);
  const shown = useRef(new Set());

  // Show toast for important events
  useEffect(() => {
    if (events.length === 0) return;
    const latest = events[0];
    if (!latest || !latest.timestamp) return;
    const key = `${latest.type}-${latest.timestamp}`;
    if (shown.current.has(key)) return;
    shown.current.add(key);

    if (latest.type === 'notification') {
      toast(latest.message || latest.title, { icon: 'i', duration: 4000 });
    } else if (latest.type === 'enrichment_progress' && latest.progress === 100) {
      toast.success(`Enrichment complete: ${latest.lead_name}`, { duration: 3000 });
    } else if (latest.type === 'mining_progress' && latest.progress === 100) {
      toast.success(`Mining complete: ${latest.leads_found} leads found`, { duration: 3000 });
    }
  }, [events]);

  if (events.length === 0 && !connected) {
    // Still show the component but in collapsed state
  }

  return (
    <div className="card-interactive overflow-hidden" data-testid="live-activity-feed">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-surface-alt/50 transition-colors"
        data-testid="live-feed-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${connected ? 'bg-primary animate-pulse-soft' : 'bg-text-muted'}`} />
            <span className="text-xs font-bold uppercase tracking-widest text-text-muted">Live Activity</span>
          </div>
          {events.length > 0 && (
            <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">{events.length}</span>
          )}
        </div>
        <i className={`fa-solid fa-chevron-${expanded ? 'up' : 'down'} text-text-muted text-xs`} />
      </button>

      {expanded && (
        <div className="border-t border-border max-h-[250px] overflow-y-auto">
          {events.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-xs text-text-muted">No real-time events yet. Run an enrichment or mining job to see live updates.</p>
            </div>
          ) : events.map((event, i) => (
            <div key={`${event.type}-${event.timestamp}-${i}`} className="flex items-start gap-3 px-5 py-3 border-b border-border/30 hover:bg-surface-alt/30 transition-colors animate-fade-in">
              <div className="w-7 h-7 rounded-lg bg-surface-alt flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={`fa-solid ${EVENT_ICONS[event.type] || 'fa-circle text-text-muted'} text-xs`} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-text-primary">{event.message || event.title || event.details || 'Event'}</p>
                {event.lead_name && <p className="text-[11px] text-text-muted">Lead: {event.lead_name}</p>}
                {event.progress !== undefined && event.progress < 100 && (
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex-1 h-1 bg-surface-alt rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${event.progress}%` }} />
                    </div>
                    <span className="text-[10px] text-text-muted tabular-nums">{event.progress}%</span>
                  </div>
                )}
                <p className="text-[10px] text-text-muted mt-0.5">{event.timestamp ? new Date(event.timestamp).toLocaleTimeString() : ''}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
